const DESKTOP_QUERY = '(min-width: 1280px)'; // Tailwind xl, where this header is hidden

// Mobile drawer: top-level list, one slide-in screen per item with children,
// and the region/language bottom sheet. The drawer is position:fixed and
// covers the viewport below the bar; the bar itself stays on top so the X
// button is always reachable.
export default class HeaderV3Mobile {
    constructor(element, header) {
        this.el = element;
        this.header = header;
        this.bar = element.querySelector('.hv3-mbar');
        this.burger = element.querySelector('[data-hv3-burger]');
        this.drawer = element.querySelector('[data-hv3-drawer]');
        this.main = element.querySelector('[data-hv3-drawer-main]');
        this.foot = element.querySelector('.hv3-drawer-foot');
        this.subs = Array.from(element.querySelectorAll('[data-hv3-sub]'));
        this.sheet = element.querySelector('[data-hv3-sheet]');

        this.isOpen = false;
        this.openSub = null;
        this.sheetOpen = false;
        this.lastTopItem = null;

        this.bindDrawer();
        this.bindSubs();
        this.bindSheet();
        this.bindGlobal();
    }

    // ---- Drawer ------------------------------------------------------------

    bindDrawer() {
        this.burger.addEventListener('click', () => (this.isOpen ? this.closeDrawer() : this.openDrawer()));
    }

    openDrawer() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.layout();

        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.burger.setAttribute('aria-expanded', 'true');
        this.header.root.classList.add('is-menu-open');
        this.header.setOpen(true);

        document.body.classList.add('overflow-hidden');
        this.setZendeskHidden(true);

        // Keyboard users land in the menu, not on the page behind it.
        const first = this.focusableInDrawer()[0];
        if (first) first.focus({ preventScroll: true });
    }

    // Focusable controls of the open menu, in Tab order: the burger (close)
    // then whatever is currently visible in the drawer — the main list, or the
    // open sub-screen. Hidden screens are excluded via computed visibility.
    focusableInDrawer() {
        const visible = (el) => el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
        const inDrawer = Array.from(this.drawer.querySelectorAll('button, a[href]')).filter((el) => !el.disabled && visible(el));
        return [this.burger, ...inDrawer];
    }

    // The Zendesk launcher sits above the drawer's Sign up footer. It is loaded
    // lazily on the visitor's first click — which is often the burger itself —
    // so a one-off check would miss it; keep looking for a few seconds after
    // opening and hide it as soon as it exists.
    setZendeskHidden(hidden) {
        clearInterval(this.zendeskTimer);

        const apply = () => {
            if (typeof zE === 'undefined') return false;
            zE('messenger', hidden ? 'hide' : 'show');
            return true;
        };

        if (apply() || !hidden) return;

        let attempts = 0;
        this.zendeskTimer = setInterval(() => {
            if (apply() || ++attempts >= 20 || !this.isOpen) {
                clearInterval(this.zendeskTimer);
            }
        }, 250);
    }

    closeDrawer() {
        if (!this.isOpen) return;
        this.isOpen = false;

        this.closeSheet();
        this.closeSub(false);

        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.burger.setAttribute('aria-expanded', 'false');
        this.header.root.classList.remove('is-menu-open');
        this.header.setOpen(false);

        document.body.classList.remove('overflow-hidden');
        this.setZendeskHidden(false);
        this.burger.focus({ preventScroll: true });
    }

    // The drawer starts under the bar (whose offset includes the promotion
    // bar when that is inside the fixed header) and the sub-screens stop above
    // the Sign up footer. Both are measured, not assumed.
    layout() {
        const top = Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom));
        const foot = this.foot ? Math.round(this.foot.offsetHeight) : 0;
        this.drawer.style.setProperty('--hv3-drawer-top', top + 'px');
        this.drawer.style.setProperty('--hv3-foot-h', foot + 'px');
    }

    // ---- Sub-screens -------------------------------------------------------

    bindSubs() {
        this.el.querySelectorAll('[data-hv3-open]').forEach((button) => {
            button.addEventListener('click', () => {
                this.lastTopItem = button;
                this.openSubById(button.dataset.hv3Open);
            });
        });

        this.el.querySelectorAll('[data-hv3-back]').forEach((button) => {
            button.addEventListener('click', () => this.closeSub(true));
        });
    }

    openSubById(id) {
        const sub = this.subs.find((candidate) => candidate.dataset.hv3Sub === id);
        if (!sub || this.openSub === sub) return;

        this.closeSub(false);
        this.openSub = sub;

        const scroll = sub.querySelector('.hv3-sub-scroll');
        if (scroll) scroll.scrollTop = 0;

        sub.classList.add('is-open');
        sub.setAttribute('aria-hidden', 'false');
        this.drawer.classList.add('has-sub');
        if (this.lastTopItem) this.lastTopItem.setAttribute('aria-expanded', 'true');

        const back = sub.querySelector('[data-hv3-back]');
        if (back) back.focus({ preventScroll: true });
    }

    closeSub(refocus) {
        if (!this.openSub) return;

        this.openSub.classList.remove('is-open');
        this.openSub.setAttribute('aria-hidden', 'true');
        this.openSub = null;
        this.drawer.classList.remove('has-sub');

        if (this.lastTopItem) {
            this.lastTopItem.setAttribute('aria-expanded', 'false');
            if (refocus) this.lastTopItem.focus({ preventScroll: true });
        }
    }

    // ---- Region / language sheet -------------------------------------------

    bindSheet() {
        if (!this.sheet) return;

        this.sheetOpener = this.el.querySelector('[data-hv3-sheet-open]');
        this.sheetOpener.addEventListener('click', () => this.openSheet());

        this.el.querySelectorAll('[data-hv3-sheet-close]').forEach((control) => {
            control.addEventListener('click', () => this.closeSheet(true));
        });

        // Picking a region: let the selection render, then slide the sheet away.
        this.header.root.addEventListener('hv3:region-change', () => {
            if (this.sheetOpen) setTimeout(() => this.closeSheet(true), 220);
        });

        // The sheet is modal (aria-modal): keep Tab inside it while it is open.
        // Focus returns to the opener in closeSheet(true).
        this.sheet.addEventListener('keydown', (event) => {
            if (event.key !== 'Tab') return;
            const focusable = Array.from(this.sheet.querySelectorAll('button, a[href]'))
                .filter((el) => !el.disabled && el.offsetParent !== null);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        });
    }

    openSheet() {
        if (!this.sheet || this.sheetOpen) return;
        this.sheetOpen = true;
        this.el.classList.add('is-sheet-open');
        this.sheet.setAttribute('aria-hidden', 'false');
        this.sheetOpener.setAttribute('aria-expanded', 'true');

        const close = this.sheet.querySelector('[data-hv3-sheet-close]');
        if (close) close.focus({ preventScroll: true });
    }

    closeSheet(refocus = false) {
        if (!this.sheet || !this.sheetOpen) return;
        this.sheetOpen = false;
        this.el.classList.remove('is-sheet-open');
        this.sheet.setAttribute('aria-hidden', 'true');
        this.sheetOpener.setAttribute('aria-expanded', 'false');
        if (refocus) this.sheetOpener.focus({ preventScroll: true });
    }

    // ---- Document-level ----------------------------------------------------

    bindGlobal() {
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || !this.isOpen) return;
            if (this.sheetOpen) {
                this.closeSheet(true);
            } else if (this.openSub) {
                this.closeSub(true);
            } else {
                this.closeDrawer();
                this.burger.focus();
            }
        });

        // The drawer covers the page: keep Tab/Shift+Tab inside burger + drawer
        // while it is open. The region sheet, when open, runs its own loop.
        this.el.addEventListener('keydown', (event) => {
            if (event.key !== 'Tab' || !this.isOpen || this.sheetOpen) return;
            const focusable = this.focusableInDrawer();
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || !focusable.includes(active))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || !focusable.includes(active))) {
                event.preventDefault();
                first.focus();
            }
        });

        window.addEventListener('resize', () => {
            if (this.isOpen) this.layout();
        });

        // Growing past the breakpoint swaps in the desktop header; release the
        // page scroll lock and reset the drawer.
        const media = window.matchMedia(DESKTOP_QUERY);
        media.addEventListener('change', (event) => {
            if (event.matches) this.closeDrawer();
        });
    }
}
